import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Video, ExternalLink, Cloud, RefreshCw, Target } from "lucide-react";
import SnapshotListSection from "./inventory/SnapshotListSection";
import { api } from "../lib/api";
import { useAuth, isAdmin } from "../contexts/AuthContext";

export default function SportsVisioAnalytics({ playerId }) {
  const { user } = useAuth();
  const canEdit = isAdmin(user);
  const [state, setState] = useState({ games: [], season_averages: null });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.listSportsVisio(playerId);
      setState(data || { games: [], season_averages: null });
    } catch (e) {
      toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [playerId]);

  const avg = state.season_averages;
  const N = (n, d=1) => (n === null || n === undefined ? "—" : Number(n).toFixed(d));

  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8" data-testid="sportsvisio-analytics">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Cloud className="w-5 h-5 text-[var(--org-accent)]" />
          <div>
            <h2 className="font-heading uppercase text-2xl tracking-[0.2em] text-white">Analytics</h2>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#a0a0a0] mt-0.5 flex items-center gap-2">
              <span>Live from SportsVisio</span>
              {loading && <Loader2 className="w-3 h-3 animate-spin" />}
            </div>
          </div>
          {avg && <span className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0] ml-2">{avg.games_played} games</span>}
        </div>
        {canEdit && (
          <button onClick={load} disabled={loading}
            className="flex items-center gap-2 px-4 py-2 border border-[var(--org-accent)]/40 text-[var(--org-accent)] text-[10px] uppercase tracking-[0.3em] font-bold hover:border-[var(--org-accent)] hover:bg-[var(--org-accent)]/5 disabled:opacity-50"
            data-testid="sv-refresh-btn">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        )}
      </div>

      {avg && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6" data-testid="sv-season-averages">
          {[["PTS","pts"],["REB","reb"],["AST","ast"],["MIN","min"],["+/-","plus_minus"],["USG%","usage_pct"]].map(([lbl,k]) => (
            <div key={k} className="border border-white/10 bg-[#101010] px-4 py-3">
              <div className="text-[9px] uppercase tracking-[0.3em] text-[#a0a0a0]">{lbl}</div>
              <div className="font-heading text-3xl text-white mt-0.5">{N(avg[k])}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mb-6"><SnapshotListSection playerId={playerId} field="key_performance_indicators" title="Key Performance Indicators" subtitle="Dominate these areas for increased production and winning impact" icon={Target} numbered accent testid="kpi-section" /></div>

      {loading ? (
        <div className="flex items-center justify-center py-10 gap-2 text-[#a0a0a0]"><Loader2 className="w-4 h-4 animate-spin" /> loading…</div>
      ) : state.games.length === 0 ? (
        <div className="border border-dashed border-white/10 py-16 text-center text-[#757575] text-sm" data-testid="sv-empty">
          No completed games on SportsVisio yet. Games appear here automatically once SportsVisio marks them <span className="text-[#a0a0a0] font-mono">final-approved</span>.
        </div>
      ) : (
        <ul className="space-y-3">
          {state.games.map((g) => (
            <li key={g.id} className="border border-white/10 bg-[#101010]" data-testid={`sv-game-${g.id}`}>
              <div className="flex items-center justify-between px-5 py-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--org-accent)]">{g.date}</div>
                    <div className="font-heading text-lg text-white uppercase tracking-tight">{g.home_away === "Away" ? "@ " : "vs "}{g.opponent}</div>
                    {g.result && <span className="text-xs text-[#a0a0a0]">{g.result}</span>}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#c0c0c0]">
                    <span><span className="text-[#757575]">PTS </span>{N(g.pts, 0)}</span>
                    <span><span className="text-[#757575]">REB </span>{N(g.reb, 0)}</span>
                    <span><span className="text-[#757575]">AST </span>{N(g.ast, 0)}</span>
                    <span><span className="text-[#757575]">MIN </span>{N(g.min, 0)}</span>
                    <span><span className="text-[#757575]">FG% </span>{N(g.fg_pct)}</span>
                    <span><span className="text-[#757575]">3P% </span>{N(g.fg3_pct)}</span>
                    <span><span className="text-[#757575]">+/- </span>{N(g.plus_minus, 0)}</span>
                    <span><span className="text-[#757575]">USG% </span>{N(g.usage_pct)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-4">
                  {g.video_url && <a href={g.video_url} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1 px-3 py-1.5 border border-white/20 text-[10px] uppercase tracking-[0.25em] font-bold text-white hover:border-[var(--org-accent)] hover:text-[var(--org-accent)]"
                    data-testid={`sv-video-${g.id}`}><Video className="w-3 h-3" /> Highlights</a>}
                  {g.shot_chart_url && <a href={g.shot_chart_url} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1 px-3 py-1.5 border border-white/20 text-[10px] uppercase tracking-[0.25em] font-bold text-white hover:border-[var(--org-accent)] hover:text-[var(--org-accent)]"
                    data-testid={`sv-shot-${g.id}`}><ExternalLink className="w-3 h-3" /> Shot Chart</a>}
                </div>
              </div>
              {g.ai_insight && (
                <div className="px-5 pb-4 pt-1 border-t border-white/5">
                  <div className="text-[10px] uppercase tracking-[0.4em] text-[var(--org-accent)] mb-1">AI Insight</div>
                  <div className="text-sm text-[#d8d8d8] whitespace-pre-wrap">{g.ai_insight}</div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
